import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Image as ImageIcon, Loader2, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import AdminShell from "@/components/admin/AdminShell";
import StatCard from "@/components/admin/StatCard";
import AssetPicker from "@/components/vault/AssetPicker";
import { adminApi } from "@/lib/adminApi";

/**
 * Founder Portal → /admin/site-media
 *
 * Table of every fixed website slot (Home hero, Agora chapter art, journal
 * default cover…). Each row shows: label, current mapping (Content ID +
 * asset title + status), a preview thumbnail, and a "Choose from Vault"
 * button that opens AssetPicker.
 *
 * Only Published assets ever surface publicly — the backend enforces that
 * in `site_media_service.resolve_slot()`. A draft/archived asset assigned
 * to a slot is shown in this admin table (with an amber "asset_draft" tag)
 * but the public site keeps rendering the fallback URL for that slot.
 */

const SOURCE_LABELS = {
  vault: "Vault",
  fallback: "Fallback (unmapped)",
  fallback_status: "Fallback (asset not published)",
  fallback_missing: "Fallback (asset missing)",
  fallback_no_url: "Fallback (asset has no URL)",
};

const SOURCE_TONES = {
  vault: "border-emerald-400/40 text-emerald-300 bg-emerald-400/5",
  fallback: "border-white/15 text-bone/60 bg-white/[0.02]",
  fallback_status: "border-amber-400/50 text-amber-300 bg-amber-400/5",
  fallback_missing: "border-red-400/60 text-red-300 bg-red-400/10",
  fallback_no_url: "border-red-400/60 text-red-300 bg-red-400/10",
};

export default function AdminSiteMedia() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [pickingSlot, setPickingSlot] = useState(null); // slot key being edited

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const data = await adminApi.listSiteMedia();
      setSlots(data.slots || []);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const assign = useCallback(async (slot, contentId) => {
    try {
      await adminApi.setSiteMediaSlot(slot, contentId);
      await load();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }, [load]);

  const clear = useCallback(async (slot) => {
    if (!confirm(`Clear the mapping for "${slot}"? The site will fall back to the built-in default image.`)) return;
    try {
      await adminApi.clearSiteMediaSlot(slot);
      await load();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }, [load]);

  // Aggregate stats for the header cards
  const stats = slots.reduce(
    (acc, s) => {
      acc.total += 1;
      if (s.resolved_source === "vault") acc.vault += 1;
      else if (s.resolved_source === "fallback_status") acc.fallback_status += 1;
      else acc.fallback += 1;
      return acc;
    },
    { total: 0, vault: 0, fallback: 0, fallback_status: 0 },
  );

  return (
    <AdminShell active="site-media">
      <div className="space-y-6" data-testid="admin-site-media">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-bone/60 hover:text-gold mb-4" data-testid="site-media-back">
              <ArrowLeft size={12} strokeWidth={1.5} /> Back to Command Center
            </Link>
            <p className="text-[11px] tracking-[0.32em] uppercase text-gold">Content Vault · Phase 3b-2</p>
            <h1 className="font-serif text-3xl md:text-4xl text-bone mt-1">Site Media Registry</h1>
            <p className="text-[12.5px] text-bone/60 mt-2 max-w-2xl leading-relaxed">
              Link fixed website slots to published Content Vault assets. Only <em>Published</em> assets surface publicly; draft or archived assignments silently fall back to the built-in default image.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="site-media-refresh"
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/40 px-3 py-2 hover:bg-gold/10"
          >
            <RefreshCw size={12} strokeWidth={1.75} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard testId="sm-stat-total"     label="Total slots"        value={stats.total} />
          <StatCard testId="sm-stat-vault"     label="Live from Vault"    value={stats.vault} />
          <StatCard testId="sm-stat-fallback"  label="Unmapped fallback"  value={stats.fallback} />
          <StatCard testId="sm-stat-wip"       label="Assigned but draft" value={stats.fallback_status} />
        </div>

        {err && <p className="text-red-400 text-sm" data-testid="site-media-err">{err}</p>}

        {/* Slot table */}
        <div className="border border-white/10 divide-y divide-white/10" data-testid="site-media-list">
          {slots.map((s) => (
            <div key={s.slot} className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-[112px_1fr_auto] gap-4 items-start" data-testid={`site-media-row-${s.slot}`}>
              {/* Preview thumbnail */}
              <div className="w-28 h-28 bg-black border border-white/10 overflow-hidden flex-shrink-0">
                {s.resolved_url ? (
                  <img src={s.resolved_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-bone/30"><ImageIcon size={20} /></div>
                )}
              </div>

              {/* Metadata */}
              <div className="min-w-0 space-y-1.5">
                <p className="font-serif text-bone text-base">{s.label}</p>
                <p className="text-[11px] text-bone/50 font-mono">{s.slot}</p>
                <p className="text-[12px] text-bone/60 leading-relaxed">{s.description}</p>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <span className={`inline-flex items-center text-[10px] tracking-[0.22em] uppercase border px-2 py-0.5 ${SOURCE_TONES[s.resolved_source] || SOURCE_TONES.fallback}`} data-testid={`site-media-source-${s.slot}`}>
                    {SOURCE_LABELS[s.resolved_source] || s.resolved_source || "unknown"}
                  </span>
                  {s.content_id && (
                    <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase border border-gold/40 text-gold px-2 py-0.5 font-mono">
                      <Sparkles size={9} strokeWidth={1.75} /> {s.content_id}
                    </span>
                  )}
                  {s.asset?.title && (
                    <span className="text-[11px] text-bone/50 italic">{s.asset.title}</span>
                  )}
                  {s.asset?.status && s.asset.status !== "published" && (
                    <span className="inline-flex items-center text-[10px] tracking-[0.22em] uppercase border border-amber-400/50 text-amber-300 px-1.5 py-0.5">
                      {s.asset.status}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex md:flex-col gap-2 md:min-w-[140px]">
                <button
                  type="button"
                  onClick={() => setPickingSlot(s.slot)}
                  data-testid={`site-media-pick-${s.slot}`}
                  className="inline-flex items-center justify-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-gold border border-gold/50 px-3 py-2 hover:bg-gold/10 whitespace-nowrap"
                >
                  <Sparkles size={11} strokeWidth={1.75} /> {s.content_id ? "Replace" : "Assign"}
                </button>
                {s.content_id && (
                  <button
                    type="button"
                    onClick={() => clear(s.slot)}
                    data-testid={`site-media-clear-${s.slot}`}
                    className="inline-flex items-center justify-center gap-1.5 text-[10px] tracking-[0.24em] uppercase text-bone/60 border border-white/15 px-3 py-2 hover:text-red-400 hover:border-red-400/40"
                  >
                    <Trash2 size={11} strokeWidth={1.75} /> Clear
                  </button>
                )}
              </div>
            </div>
          ))}
          {slots.length === 0 && !loading && (
            <p className="p-6 text-bone/60 text-sm">No slots configured.</p>
          )}
          {loading && (
            <div className="p-6 flex items-center gap-2 text-bone/60 text-sm">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          )}
        </div>
      </div>

      {/* AssetPicker modal */}
      {pickingSlot && (
        <AssetPicker
          open={!!pickingSlot}
          onClose={() => setPickingSlot(null)}
          // Site Media needs EVERY published asset — regardless of whether
          // its AI enrichment has been founder-confirmed. `only_confirmed:
          // false` disables the legacy curator filter; `status: "published"`
          // enforces the publish-workflow gate at the API layer.
          defaultFilter={{ only_confirmed: false, status: "published" }}
          title="Choose a Published Vault asset"
          onSelect={(asset) => {
            const slot = pickingSlot;
            setPickingSlot(null);
            if (asset?.content_id) assign(slot, asset.content_id);
          }}
        />
      )}
    </AdminShell>
  );
}
